import { request } from "../../../../services/api.ts"
import { downloadBlob } from "../../../../utils/download-file.ts"
import { PerformanceLoadError } from "../api"
import type {
  MemberMilestonesResponse,
  MemberOverviewResponse,
  MemberProjectsResponse,
  MemberQuery,
  MemberTypesResponse
} from "./types"

const memberPath = (id: string, tail = "") =>
  `/dashboard/team-performance/members/${id}/${tail}`

const rangeParams = (q: MemberQuery) => ({
  grain: q.grain,
  ...(q.from ? { from: q.from } : {}),
  ...(q.to ? { to: q.to } : {}),
  ...(q.projectType ? { project_type: q.projectType } : {})
})

const get = async <T>(
  path: string,
  params: Record<string, unknown>
): Promise<T> => {
  const response = await request.api.get<T>(path, params)
  if (!response.ok || !response.data)
    throw new PerformanceLoadError(response.status)
  return response.data
}

export const fetchMemberOverview = (id: string, q: MemberQuery) =>
  get<MemberOverviewResponse>(memberPath(id), {
    ...rangeParams(q),
    compare: q.compare
  })

export const fetchMemberProjects = (id: string, q: MemberQuery) =>
  get<MemberProjectsResponse>(memberPath(id, "projects/"), {
    ...rangeParams(q),
    ...(q.period ? { period: q.period } : {}),
    ...(q.milestone ? { milestone: q.milestone } : {}),
    ...(q.flag ? { flag: q.flag } : {})
  })

export const fetchMemberTypes = (id: string, q: MemberQuery) =>
  get<MemberTypesResponse>(memberPath(id, "project-types/"), rangeParams(q))

export const fetchMemberMilestones = (id: string, q: MemberQuery) =>
  get<MemberMilestonesResponse>(memberPath(id, "milestones/"), {
    ...rangeParams(q),
    min_occurrences: q.minOccurrences ?? 3
  })

export const memberPaths = {
  overview: memberPath,
  projects: (id: string) => memberPath(id, "projects/")
}
export { PerformanceLoadError, rangeParams }

// The server writes the file (filters, timezone and score versions in its header),
// so the download is exactly the figures on screen.
export const downloadPerformanceCsv = async (
  path: string,
  params: Record<string, unknown>,
  filename: string
) => {
  const response = await request.api.get<Blob>(
    path,
    { ...params, export: "csv" },
    { responseType: "blob" }
  )
  if (!response.ok || !response.data)
    throw new PerformanceLoadError(response.status)
  downloadBlob(response.data, filename)
}
